"use client";

import { useEffect, useEffectEvent, useState, type RefObject } from "react";

/* Also re-measures on transitionend: the auto-hiding header's slide moves sticky offsets with no scroll. */
export const useScrollFlag = (
  ref: RefObject<HTMLElement | null>,
  enabled: boolean,
  measure: (el: HTMLElement) => boolean,
): boolean => {
  const [flag, setFlag] = useState(false);
  const check = useEffectEvent((el: HTMLElement) => setFlag(measure(el)));

  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) {
      setFlag(false);
      return;
    }

    let frame = 0;
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        check(el);
      });
    };

    check(el);
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    window.addEventListener("transitionend", schedule);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("transitionend", schedule);
    };
  }, [ref, enabled]);

  return flag;
};
